import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ModalContainer } from 'components/core';
import { ModalWrapper } from 'shared/modals/v2-wrapper';
import { withModalControlsHook, ModalComponentProps } from 'components/modals/with-modal-controls-hook';
import { BankIcon } from 'assets/bank-icon';
import ExitIcon from 'assets/exit-icon';
import useErnie from 'shared/hooks/use-ernie';
import { tracker } from 'src/utils/analytics';
import { useDutchiePay } from 'src/payments/hooks';
import { useUnenrollPayByBankUser } from 'src/payments/hooks/use-unenroll-pay-by-bank-user/use-unenroll-pay-by-bank-user';
import { UNLINK_BANK_MODAL_TITLE } from 'src/payments/constants';

const UNLINK_BENEFITS = ['Express checkouts', 'Secure bank payments', 'Faster pickups'];

const UnlinkBankModalInner = ({ onClose, containerProps }: ModalComponentProps): JSX.Element => {
  const showErnie = useErnie();
  const { activeBankAccount } = useDutchiePay();
  const { handleUnenrollPayByBank } = useUnenrollPayByBankUser();
  const [isLoading, setIsLoading] = useState(false);

  const financialInstitutionLogo =
    activeBankAccount?.financialInstitutionLogo &&
    `data:image/png;base64, ${activeBankAccount.financialInstitutionLogo}`;

  useEffect(() => {
    tracker.dutchiePayUnlinkModalViewed({});
  }, []);

  const handleCancel = (): void => {
    tracker.dutchiePayUnlinkCancelled({});
    onClose();
  };

  const handleRemove = async (): Promise<void> => {
    tracker.dutchiePayUnlinkConfirmed({});
    setIsLoading(true);
    try {
      const result = await handleUnenrollPayByBank({
        changedFields: [],
        reason: 'consumer_explicit_unlink',
        surface: 'bank_account',
      });
      if (result.success) {
        tracker.dutchiePayUnlinkSuccess({});
        onClose();
      } else {
        tracker.dutchiePayUnlinkError({});
        showErnie('Something went wrong removing your bank account. Please try again.', 'error');
      }
    } catch {
      tracker.dutchiePayUnlinkError({});
      showErnie('Something went wrong removing your bank account. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledModalContainer width='460px' padding='32px' {...containerProps}>
      <ExitButton type='button' aria-label='Close' onClick={handleCancel}>
        <StyledExitIcon />
      </ExitButton>

      <LogoRow>
        {financialInstitutionLogo ? (
          <BankLogo src={financialInstitutionLogo} alt='Bank logo' />
        ) : (
          <BankIcon height={48} width={48} />
        )}
      </LogoRow>

      <Title>{UNLINK_BANK_MODAL_TITLE}</Title>

      <BodyText>Are you sure you want to remove your bank account? You&apos;ll lose out on:</BodyText>

      <BenefitsList>
        {UNLINK_BENEFITS.map((benefit) => (
          <BenefitItem key={benefit}>
            <XCircle>
              <StyledExitIconSmall />
            </XCircle>
            <span>{benefit}</span>
          </BenefitItem>
        ))}
      </BenefitsList>

      <ButtonRow>
        <RemoveButton type='button' onClick={handleRemove} disabled={isLoading}>
          {isLoading ? 'Removing...' : 'Remove Payment'}
        </RemoveButton>
        <CancelButton type='button' onClick={handleCancel}>
          Cancel
        </CancelButton>
      </ButtonRow>
    </StyledModalContainer>
  );
};

const UnlinkBankModalWrapper = (props: ModalComponentProps): JSX.Element => (
  <ModalWrapper open zIndex={1600}>
    <UnlinkBankModalInner {...props} />
  </ModalWrapper>
);

const [UnlinkBankModal, useUnlinkBankModal] = withModalControlsHook(UnlinkBankModalWrapper);

export { UnlinkBankModal, useUnlinkBankModal, UnlinkBankModalInner };

const StyledModalContainer = styled(ModalContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  gap: 16px;
`;

const ExitButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 0;
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.grey[45]};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryBlack};
  }
`;

const StyledExitIcon = styled(ExitIcon)`
  width: 16px;
  height: 16px;
`;

const LogoRow = styled.div`
  margin-top: 8px;
`;

const BankLogo = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

const Title = styled.h2`
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0;
`;

const BodyText = styled.p`
  color: ${({ theme }) => theme.colors.grey[25]};
  font-size: 16px;
  line-height: 1.2;
  margin: 0;
  padding-bottom: 8px;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0 0 8px;
  margin: 0;
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: center;
  width: 100%;
`;

const BenefitItem = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  font-size: 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[25]};
`;

const XCircle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 80px;
  background: #fae8ea;
  color: #be0517;
`;

const StyledExitIconSmall = styled(ExitIcon)`
  width: 18px;
  height: 18px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: 2px solid #be0517;
  border-radius: 9999px;
  color: #be0517;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  padding: 12px 24px;
  width: 264px;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: transparent;
  border: 0;
  color: #3387e1;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;
